import Service from '@ember/service';
import type { BeniBreakdown } from 'lumen/models';
import type { Kpi } from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import type { LosChartSeriesData } from 'lumen/utils/constants/benchmark-insight/los/los-chart';
import {
  SORT_ORDER,
  type SortOrderType,
} from 'lumen/utils/constants/general-constants';

export default class BenchmarkInsightBeniChartLosServiceService extends Service {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  groupDataBySeries(seriesArray: { data: LosChartSeriesData }[]) {
    // First, flatten the data arrays into a single array of data points, each with an id indicating its position in the original array.
    const data = seriesArray.flatMap((series) =>
      series.data.map((dataPoint, pointIndex) => ({
        id: pointIndex,
        value: dataPoint,
      })),
    );

    // Find the maximum id, which will be the length of the result array.
    const maxPointIndex = Math.max(...data.map((item) => item.id));

    // Create the result array. Each element of the array is an object containing an array of values for a specific point in the series.
    const result = Array.from({ length: maxPointIndex + 1 }, (_, i) => ({
      values: data.reduce(
        (values: (number | null)[], item) =>
          item.id === i ? [...values, item.value] : values,
        [],
      ),
    }));
    return result;
  }

  sortLosBreakdowns(
    breakdowns: BeniBreakdown[],
    kpi: Kpi,
    isDifference: boolean,
    sortDirection: SortOrderType,
  ): BeniBreakdown[] {
    return breakdowns.sort((a, b) => {
      let aValue = a.current.total[kpi];
      let bValue = b.current.total[kpi];

      if (isDifference) {
        if (a.current.total.difference && a.current.total.difference[kpi]) {
          // Sort on the difference between the comparison set and the own hotel.
          aValue = a.current.total.difference[kpi];
          bValue = b.current.total.difference[kpi];
        }
      }

      if (bValue === null) {
        return -1;
      }

      if (aValue === null) {
        return 1;
      }

      if (sortDirection === SORT_ORDER.ASCENDING) {
        return aValue - bValue;
      }

      return bValue - aValue;
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    'benchmark-insight/beni-chart-los-service': BenchmarkInsightBeniChartLosServiceService;
  }
}
